'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { PointerEvent as ReactPointerEvent, WheelEvent as ReactWheelEvent } from 'react';

export type ParkingSpotType = 'space_regular' | 'space_handi' | 'space_family' | 'space_disabled' | 'space_ev';
export type LayoutBuilderGateType = 'entry' | 'exit';

export type LayoutBuilderSpot = {
  id: string;
  label: string;
  cx: number;
  cy: number;
  width: number;
  height: number;
  rotation: number;
  type: ParkingSpotType;
};

export type LayoutBuilderGate = {
  id: string;
  label: string;
  cx: number;
  cy: number;
  width: number;
  height: number;
  rotation: number;
  type: LayoutBuilderGateType;
};

export type ParkingLayoutBuilderValue = {
  width: number;
  height: number;
  spots: LayoutBuilderSpot[];
  gates: LayoutBuilderGate[];
};

type LayoutBuilderEntity = LayoutBuilderSpot | LayoutBuilderGate;

type Point = {
  x: number;
  y: number;
};

type ViewportTransform = Point & {
  scale: number;
};

type ParkingLayoutBuilderProps = {
  value: ParkingLayoutBuilderValue;
  onChange: (next: ParkingLayoutBuilderValue) => void;
  targetSpots?: number | null;
  spotOccupancy?: Record<string, 0 | 1 | boolean>;
  disabled?: boolean;
  minimalView?: boolean;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const wrapRotation = (rotation: number) => {
  const wrapped = ((rotation % 360) + 360) % 360;
  return wrapped;
};

const normalizeDeltaDegrees = (delta: number) => {
  if (Number.isNaN(delta) || !Number.isFinite(delta)) {
    return 0;
  }
  let adjusted = delta;
  while (adjusted > 180) {
    adjusted -= 360;
  }
  while (adjusted < -180) {
    adjusted += 360;
  }
  return adjusted;
};

const createSpotId = () => `slot-${Math.random().toString(36).slice(2, 9)}`;
const createGateId = (type: LayoutBuilderGateType) => `${type}-${Math.random().toString(36).slice(2, 9)}`;

const GRID_STEP = 25;
const SNAP_GRID = GRID_STEP;
const DEFAULT_CANVAS = {
  width: 600,
  height: 400,
};

const DEFAULT_SPOT = {
  width: 48,
  height: 96,
};
const DEFAULT_GATE = {
  width: 120,
  height: 42,
};
const EDITOR_MIN_SCALE = 0.55;
const MOBILE_EDITOR_FIT_BREAKPOINT = 640;
const MINIMAL_PREVIEW_MAX_WIDTH = 920;
const MINIMAL_PREVIEW_MAX_HEIGHT = 420;

const DEFAULT_SPOT_TYPE: ParkingSpotType = 'space_regular';
const GATE_TYPE_OPTIONS: Array<{ value: LayoutBuilderGateType; label: string }> = [
  { value: 'entry', label: 'Wjazd' },
  { value: 'exit', label: 'Wyjazd' },
];
const GATE_TYPE_SET = new Set<LayoutBuilderGateType>(GATE_TYPE_OPTIONS.map((option) => option.value));
const normalizeGateType = (value: unknown): LayoutBuilderGateType =>
  typeof value === 'string' && GATE_TYPE_SET.has(value as LayoutBuilderGateType) ? (value as LayoutBuilderGateType) : 'entry';
const GATE_TYPE_LABELS: Record<LayoutBuilderGateType, string> = {
  entry: 'Wjazd',
  exit: 'Wyjazd',
};

const SPOT_TYPE_OPTIONS: Array<{ value: ParkingSpotType; label: string }> = [
  { value: 'space_regular', label: 'Miejsce parkingowe' },
  { value: 'space_handi', label: 'Miejsce dla niepełnosprawnych' },
  { value: 'space_family', label: 'Miejsce dla rodzin z dziećmi' },
  { value: 'space_disabled', label: 'Zakaz parkowania' },
  { value: 'space_ev', label: 'Miejsce z ładowarką' },
];
const SPOT_TYPE_SET = new Set<ParkingSpotType>(SPOT_TYPE_OPTIONS.map((option) => option.value));
const normalizeSpotType = (value: unknown): ParkingSpotType =>
  typeof value === 'string' && SPOT_TYPE_SET.has(value as ParkingSpotType) ? (value as ParkingSpotType) : DEFAULT_SPOT_TYPE;

const SPOT_TYPE_LABELS: Record<ParkingSpotType, string> = {
  space_regular: 'Miejsce parkingowe',
  space_handi: 'Miejsce dla niepełnosprawnych',
  space_family: 'Miejsce dla rodzin z dziećmi',
  space_disabled: 'Zakaz parkowania',
  space_ev: 'Miejsce z ładowarka',
};

const isLayoutGate = (entity: LayoutBuilderEntity): entity is LayoutBuilderGate => entity.type === 'entry' || entity.type === 'exit';
const getGateDisplayLabel = (gate: Pick<LayoutBuilderGate, 'label' | 'type'>, fallbackIndex?: number) => {
  const trimmed = gate.label.trim();
  if (trimmed.length > 0) {
    return trimmed;
  }
  if (typeof fallbackIndex === 'number' && fallbackIndex > 0) {
    return `${GATE_TYPE_LABELS[gate.type]} ${fallbackIndex}`;
  }
  return GATE_TYPE_LABELS[gate.type];
};

type ReadonlySpotOccupancyStatus = 0 | 1 | null;

const READONLY_SPOT_STATUS_TONES: Record<
  'free' | 'occupied' | 'unknown',
  {
    containerClassName: string;
    label: string;
  }
> = {
  free: {
    containerClassName:
      'border-emerald-500 bg-emerald-500 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.15)] dark:border-emerald-400 dark:bg-emerald-500 dark:text-white',
    label: 'Wolne',
  },
  occupied: {
    containerClassName:
      'border-rose-500 bg-rose-500 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.15)] dark:border-rose-400 dark:bg-rose-500 dark:text-white',
    label: 'Zajete',
  },
  unknown: {
    containerClassName:
      'border-slate-400 bg-slate-500 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.15)] dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100',
    label: 'Status nieznany',
  },
};

const READONLY_SPECIAL_TYPES: ParkingSpotType[] = ['space_handi', 'space_family', 'space_ev', 'space_disabled'];

const getSpotDisplayLabel = (label: string) => {
  const trimmed = label.trim();
  if (!trimmed.length) {
    return '--';
  }

  const prefixedLabelMatch = trimmed.match(/^(?:miejsce|spot)\s+(.+)$/i);
  return prefixedLabelMatch?.[1] ?? trimmed;
};

const renderReadonlySpotIcon = (spotType: ParkingSpotType, className: string): ReactNode => {
  switch (spotType) {
    case 'space_handi':
      return (
        <svg viewBox="0 0 76 76" className={className} fill="none" aria-hidden="true">
          <path
            fill="currentColor"
            d="M34.0417 19C36.2278 19 38 20.7722 38 22.9583C38 24.8733 36.6401 26.4707 34.8333 26.8375L35.2316 33.25H44.3333V36.4167H35.4283L35.625 39.5833H46.7083L51.629 50.6353L54.8271 49.2778L56.0644 52.1927L50.0271 54.823L50.0018 54.7662L49.9248 54.7988L44.6519 42.75H31.6667V26.1253C30.7052 25.4031 30.0833 24.2534 30.0833 22.9583C30.0833 20.7722 31.8555 19 34.0417 19ZM34.8333 58.5833C28.2749 58.5833 22.9583 53.2667 22.9583 46.7083C22.9583 41.8388 25.8893 37.6539 30.0833 35.8214V39.4083C27.7002 40.9621 26.125 43.6513 26.125 46.7083C26.125 51.5178 30.0238 55.4167 34.8333 55.4167C39.6428 55.4167 43.5417 51.5178 43.5417 46.7083L43.5376 46.6171L45.5437 51.8433C43.6286 55.8306 39.5524 58.5833 34.8333 58.5833Z"
          />
        </svg>
      );
    case 'space_family':
      return (
        <svg viewBox="0 0 512 512" className={className} fill="none" aria-hidden="true">
          <path
            fill="currentColor"
            d="M78.641 118.933C101.521 118.933 120.057 100.382 120.057 77.519C120.057 54.632 101.521 36.096 78.641 36.096C55.754 36.096 37.218 54.632 37.218 77.519C37.218 100.382 55.754 118.933 78.641 118.933Z"
          />
          <path
            fill="currentColor"
            d="M255.706 228.73V228.792C255.807 228.792 255.9 228.761 256 228.761C256.101 228.761 256.194 228.792 256.294 228.792V228.73C271.856 228.413 284.376 215.754 284.376 200.129C284.376 184.481 271.856 171.83 256.294 171.513V171.45C256.194 171.45 256.101 171.481 256 171.481C255.9 171.481 255.807 171.45 255.706 171.45V171.513C240.143 171.83 227.623 184.481 227.623 200.129C227.623 215.754 240.143 228.413 255.706 228.73Z"
          />
          <path
            fill="currentColor"
            d="M433.359 118.933C456.246 118.933 474.782 100.382 474.782 77.519C474.782 54.632 456.246 36.096 433.359 36.096C410.48 36.096 391.944 54.632 391.944 77.519C391.944 100.382 410.48 118.933 433.359 118.933Z"
          />
          <path
            fill="currentColor"
            d="M470.097 138.553H433.785H415.381C394.275 138.553 374.949 150.384 365.348 169.175L331.854 267.142C330.7 269.388 328.556 270.982 326.062 271.424C323.569 271.866 321.014 271.115 319.148 269.388L298.312 251.348C292.079 245.579 283.904 242.375 275.41 242.375H256H236.59C228.096 242.375 219.921 245.579 213.688 251.348L192.853 269.388C190.986 271.115 188.431 271.866 185.939 271.424C183.447 270.982 181.302 269.388 180.148 267.142L146.653 169.175C137.053 150.384 117.727 138.553 96.621 138.553H78.215H41.902C21.834 138.553 0 160.387 0 180.464V319.675C0 329.709 8.13 337.846 18.164 337.846H30.846L37.752 456.571C37.752 467.247 46.416 475.903 57.092 475.903H99.336C110.012 475.903 118.676 467.247 118.676 456.571L125.582 337.846L125.497 253.08C125.497 251.741 126.411 250.587 127.719 250.262C129.028 249.952 130.367 250.571 130.979 251.764L157.551 317.165C160.757 323.421 166.703 327.819 173.625 329.05C180.547 330.281 187.647 328.206 192.811 323.437L218.237 304.708C219.089 303.926 220.32 303.724 221.373 304.166C222.434 304.639 223.116 305.684 223.116 306.829L223.209 380.337L227.986 462.524C227.986 469.911 233.987 475.903 241.381 475.903H270.618C278.012 475.903 284.012 469.911 284.012 462.524L288.79 380.337L288.883 306.829C288.883 305.684 289.564 304.639 290.625 304.166C291.678 303.724 292.909 303.926 293.761 304.708L319.188 323.437C324.352 328.206 331.452 330.281 338.374 329.05C345.296 327.819 351.242 323.421 354.447 317.165L381.021 251.764C381.632 250.572 382.972 249.952 384.28 250.262C385.589 250.587 386.502 251.741 386.502 253.08L386.417 337.846L393.323 456.571C393.323 467.247 401.987 475.903 412.664 475.903H454.907C465.584 475.903 474.926 467.247 474.926 456.571L481.832 337.846H494.514C504.548 337.846 512 329.709 512 319.675V180.464C512 160.387 490.166 138.553 470.097 138.553Z"
          />
        </svg>
      );
    case 'space_ev':
      return (
        <svg viewBox="0 0 512 512" className={className} fill="none" aria-hidden="true">
          <polygon fill="currentColor" points="439.6,0 204.9,0 55.4,256 204.9,256 76.9,512 418.2,192 247.5,192" />
        </svg>
      );
    case 'space_disabled':
      return (
        <svg viewBox="0 0 412.123 412.122" className={className} fill="none" aria-hidden="true">
          <path
            fill="currentColor"
            d="M206.061 0C92.441 0 0 92.436 0 206.062C0 319.69 92.441 412.122 206.061 412.122C319.677 412.122 412.123 319.69 412.123 206.061C412.123 92.436 319.677 0 206.061 0ZM54.474 206.062C54.474 122.475 122.474 54.474 206.061 54.474C238.877 54.474 269.271 64.987 294.108 82.781L82.78 294.108C64.992 269.271 54.474 238.878 54.474 206.062ZM206.061 357.649C174.932 357.649 145.978 348.203 121.874 332.055L332.055 121.873C348.202 145.969 357.648 174.937 357.648 206.061C357.648 289.651 289.651 357.649 206.061 357.649Z"
          />
        </svg>
      );
    default:
      return null;
  }
};

const resolveReadonlySpotAppearance = (spotType: ParkingSpotType, occupancyStatus: ReadonlySpotOccupancyStatus) => {
  if (occupancyStatus === 0) {
    return {
      containerClassName: READONLY_SPOT_STATUS_TONES.free.containerClassName,
      iconClassName: 'text-white',
      labelClassName: 'text-white',
      occupancyLabel: READONLY_SPOT_STATUS_TONES.free.label,
    };
  }

  if (occupancyStatus === 1) {
    return {
      containerClassName: READONLY_SPOT_STATUS_TONES.occupied.containerClassName,
      iconClassName: 'text-white',
      labelClassName: 'text-white',
      occupancyLabel: READONLY_SPOT_STATUS_TONES.occupied.label,
    };
  }

  return {
    containerClassName: READONLY_SPOT_STATUS_TONES.unknown.containerClassName,
    iconClassName: 'text-white',
    labelClassName: 'text-white',
    occupancyLabel: READONLY_SPOT_STATUS_TONES.unknown.label,
  };
};

const createGridBackgroundStyle = (gridSize: number) => {
  return {
    backgroundColor: 'var(--grid-background, #0f172a)',
    backgroundImage: [
      'linear-gradient(90deg, var(--grid-line-strong, rgba(255,255,255,0.12)) 1px, transparent 1px)',
      'linear-gradient(180deg, var(--grid-line-strong, rgba(255,255,255,0.12)) 1px, transparent 1px)',
    ].join(','),
    backgroundSize: `${gridSize}px ${gridSize}px, ${gridSize}px ${gridSize}px`,
    backgroundPosition: '0 0, 0 0',
  };
};

const gridBackgroundStyle = createGridBackgroundStyle(GRID_STEP);

const snapToGrid = (value: number) => Math.round(value / SNAP_GRID) * SNAP_GRID;

const usePreviewScale = (
  contentWidth: number,
  contentHeight: number,
  options?: {
    minScale?: number;
    maxWidth?: number;
    maxHeight?: number;
    disableMinScaleBelowWidth?: number;
  },
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const minScale = options?.minScale ?? 0;
  const maxWidth = options?.maxWidth;
  const maxHeight = options?.maxHeight;
  const disableMinScaleBelowWidth = options?.disableMinScaleBelowWidth;

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const updateScale = (availableWidth: number) => {
      if (availableWidth <= 0) {
        return;
      }
      const widthLimit = Math.min(availableWidth, maxWidth ?? availableWidth);
      const widthScale = widthLimit / Math.max(contentWidth, 1);
      const heightScale = typeof maxHeight === 'number' ? maxHeight / Math.max(contentHeight, 1) : 1;
      const fittedScale = Math.min(1, widthScale, heightScale);
      const resolvedMinScale = typeof disableMinScaleBelowWidth === 'number' && availableWidth <= disableMinScaleBelowWidth ? 0 : minScale;
      setScale(Math.min(1, Math.max(resolvedMinScale, fittedScale)));
    };

    updateScale(element.clientWidth);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      updateScale(entry.contentRect.width);
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [contentHeight, contentWidth, disableMinScaleBelowWidth, maxHeight, maxWidth, minScale]);

  return { containerRef, scale } as const;
};

const distanceBetweenPoints = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

const midpointBetweenPoints = (a: Point, b: Point): Point => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});

const rotatedDimensions = (entity: Pick<LayoutBuilderEntity, 'width' | 'height' | 'rotation'>) => {
  const rotation = wrapRotation(entity.rotation ?? 0);
  const radians = (rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  return {
    width: entity.width * cos + entity.height * sin,
    height: entity.width * sin + entity.height * cos,
  };
};

const clampEntityCenter = (entity: LayoutBuilderEntity, canvasWidth: number, canvasHeight: number, cx: number, cy: number) => {
  const { width, height } = rotatedDimensions(entity);
  const halfW = width / 2;
  const halfH = height / 2;
  const safeWidth = Math.max(canvasWidth, width);
  const safeHeight = Math.max(canvasHeight, height);
  return {
    cx: clamp(cx, halfW, safeWidth - halfW),
    cy: clamp(cy, halfH, safeHeight - halfH),
  };
};

export function ParkingLayoutBuilder({ value, onChange, targetSpots = null, spotOccupancy, disabled = false, minimalView = false }: ParkingLayoutBuilderProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [fullscreenTransform, setFullscreenTransform] = useState<ViewportTransform>({ x: 0, y: 0, scale: 1 });
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const { containerRef: editorContainerRef, scale: editorScale } = usePreviewScale(value.width, value.height, {
    minScale: EDITOR_MIN_SCALE,
    disableMinScaleBelowWidth: MOBILE_EDITOR_FIT_BREAKPOINT,
  });
  const { containerRef: previewContainerRef, scale: previewScale } = usePreviewScale(value.width, value.height, {
    maxWidth: MINIMAL_PREVIEW_MAX_WIDTH,
    maxHeight: MINIMAL_PREVIEW_MAX_HEIGHT,
  });
  const fullscreenViewportRef = useRef<HTMLDivElement | null>(null);
  const fullscreenTransformRef = useRef<ViewportTransform>({ x: 0, y: 0, scale: 1 });
  const fullscreenPointersRef = useRef<Map<number, Point>>(new Map());
  const fullscreenGestureRef = useRef<
    { type: 'pan'; startPointer: Point; startOffset: Point } | { type: 'pinch'; startScale: number; startDistance: number; anchor: Point } | null
  >(null);
  const dragRef = useRef<{
    id: string;
    deltaX: number;
    deltaY: number;
  } | null>(null);
  const rotateRef = useRef<{
    id: string;
    startAngle: number;
    initialRotation: number;
  } | null>(null);
  const gates = value.gates;
  const allEntities = useMemo<LayoutBuilderEntity[]>(() => [...gates, ...value.spots], [gates, value.spots]);

  useEffect(() => {
    if (!selectedId) {
      return;
    }
    if (!allEntities.some((entity) => entity.id === selectedId)) {
      setSelectedId(null);
    }
  }, [allEntities, selectedId]);

  useEffect(() => {
    fullscreenTransformRef.current = fullscreenTransform;
  }, [fullscreenTransform]);

  const emitChange = useCallback(
    (next: ParkingLayoutBuilderValue) => {
      onChange({
        width: Math.max(120, next.width),
        height: Math.max(120, next.height),
        spots: next.spots.map((spot) => ({
          ...spot,
          type: normalizeSpotType(spot.type),
        })),
        gates: (next.gates ?? []).map((gate) => ({
          ...gate,
          type: normalizeGateType(gate.type),
        })),
      });
    },
    [onChange],
  );

  useEffect(() => {
    if (value.spots.some((spot) => !spot.type) || gates.some((gate) => !gate.type)) {
      emitChange({
        ...value,
        spots: value.spots.map((spot) => ({
          ...spot,
          type: normalizeSpotType(spot.type),
        })),
        gates: gates.map((gate) => ({
          ...gate,
          type: normalizeGateType(gate.type),
        })),
      });
    }
  }, [emitChange, gates, value]);

  const updateGate = (id: string, mutator: (gate: LayoutBuilderGate) => LayoutBuilderGate) => {
    emitChange({
      ...value,
      gates: gates.map((gate) => (gate.id === id ? mutator(gate) : gate)),
    });
  };

  const updateSpot = (id: string, mutator: (spot: LayoutBuilderSpot) => LayoutBuilderSpot) => {
    emitChange({
      ...value,
      spots: value.spots.map((spot) => (spot.id === id ? mutator(spot) : spot)),
    });
  };

  const updateEntity = (id: string, mutator: (entity: LayoutBuilderEntity) => LayoutBuilderEntity) => {
    const target = allEntities.find((entity) => entity.id === id);
    if (!target) {
      return;
    }
    if (isLayoutGate(target)) {
      updateGate(id, (gate) => mutator(gate) as LayoutBuilderGate);
      return;
    }
    updateSpot(id, (spot) => mutator(spot) as LayoutBuilderSpot);
  };

  const canAddSpot = !disabled && (targetSpots === null || targetSpots <= 0 || value.spots.length < targetSpots);

  const remainingSpots = targetSpots && targetSpots > 0 ? Math.max(targetSpots - value.spots.length, 0) : null;

  const addSpot = () => {
    if (!canAddSpot) {
      return;
    }
    const id = createSpotId();
    const label = `${value.spots.length + 1}`;
    const cx = snapToGrid(value.width / 2);
    const cy = snapToGrid(value.height / 2);
    const nextSpot: LayoutBuilderSpot = {
      id,
      label,
      cx,
      cy,
      width: DEFAULT_SPOT.width,
      height: DEFAULT_SPOT.height,
      rotation: 0,
      type: DEFAULT_SPOT_TYPE,
    };
    emitChange({
      ...value,
      spots: [...value.spots, nextSpot],
    });
    setSelectedId(id);
  };

  const addGate = (type: LayoutBuilderGateType) => {
    if (disabled) {
      return;
    }
    const cx = snapToGrid(value.width / 2);
    const preferredCy =
      type === 'entry'
        ? DEFAULT_GATE.height / 2 + SNAP_GRID
        : Math.max(DEFAULT_GATE.height / 2 + SNAP_GRID, value.height - DEFAULT_GATE.height / 2 - SNAP_GRID);
    const cy = snapToGrid(preferredCy);
    const nextIndex = gates.filter((gate) => gate.type === type).length + 1;
    const nextGate: LayoutBuilderGate = {
      id: createGateId(type),
      label: `${GATE_TYPE_LABELS[type]} ${nextIndex}`,
      cx,
      cy,
      width: DEFAULT_GATE.width,
      height: DEFAULT_GATE.height,
      rotation: 0,
      type,
    };
    emitChange({
      ...value,
      gates: [...gates, nextGate],
    });
    setSelectedId(nextGate.id);
  };

  const removeSpot = (id: string) => {
    emitChange({
      ...value,
      spots: value.spots.filter((spot) => spot.id !== id),
    });
    if (selectedId === id) {
      setSelectedId(null);
    }
  };

  const removeGate = (id: string) => {
    emitChange({
      ...value,
      gates: gates.filter((gate) => gate.id !== id),
    });
    if (selectedId === id) {
      setSelectedId(null);
    }
  };

  const getPointerPosition = (event: ReactPointerEvent) => {
    const canvasRect = canvasRef.current?.getBoundingClientRect();
    if (!canvasRect) {
      return null;
    }
    const scale = Math.max(editorScale, 0.001);
    return {
      x: (event.clientX - canvasRect.left) / scale,
      y: (event.clientY - canvasRect.top) / scale,
    };
  };

  const handleDragPointerDown = (event: ReactPointerEvent<HTMLButtonElement>, entity: LayoutBuilderEntity) => {
    if (disabled) {
      return;
    }
    const pointer = getPointerPosition(event);
    if (!pointer) {
      return;
    }

    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = {
      id: entity.id,
      deltaX: pointer.x - entity.cx,
      deltaY: pointer.y - entity.cy,
    };
    setSelectedId(entity.id);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleRotationPointerDown = (event: ReactPointerEvent<HTMLButtonElement>, entity: LayoutBuilderEntity) => {
    if (disabled) {
      return;
    }
    const pointer = getPointerPosition(event);
    if (!pointer) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const angle = Math.atan2(pointer.y - entity.cy, pointer.x - entity.cx);
    rotateRef.current = {
      id: entity.id,
      startAngle: angle,
      initialRotation: wrapRotation(entity.rotation ?? 0),
    };
    setSelectedId(entity.id);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!dragRef.current && !rotateRef.current) {
      return;
    }
    event.preventDefault();
    const pointer = getPointerPosition(event);
    if (!pointer) {
      return;
    }

    if (rotateRef.current) {
      const { id, startAngle, initialRotation } = rotateRef.current;
      const targetEntity = allEntities.find((candidate) => candidate.id === id);
      if (!targetEntity) {
        rotateRef.current = null;
        return;
      }
      const currentAngle = Math.atan2(pointer.y - targetEntity.cy, pointer.x - targetEntity.cx);
      const rawDeltaDegrees = ((currentAngle - startAngle) * 180) / Math.PI;
      const deltaDegrees = normalizeDeltaDegrees(rawDeltaDegrees);
      const nextRotation = wrapRotation(Math.round(initialRotation + deltaDegrees));
      updateEntity(id, (entity) => ({
        ...entity,
        rotation: nextRotation,
      }));
      return;
    }

    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    const rawCx = pointer.x - drag.deltaX;
    const rawCy = pointer.y - drag.deltaY;
    updateEntity(drag.id, (entity) => {
      const snappedCx = snapToGrid(rawCx);
      const snappedCy = snapToGrid(rawCy);
      const clamped = clampEntityCenter(entity, value.width, value.height, snappedCx, snappedCy);
      return {
        ...entity,
        cx: clamped.cx,
        cy: clamped.cy,
      };
    });
  };

  const finishDragging = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (dragRef.current || rotateRef.current) {
      dragRef.current = null;
      rotateRef.current = null;
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const sanitizeRotationInput = (value: number) => {
    if (!Number.isFinite(value)) {
      return 0;
    }
    const rounded = Math.round(value);
    return Math.min(Math.max(rounded, 0), 360);
  };

  const setSpotRotation = (id: string, rotation: number) => {
    const sanitized = sanitizeRotationInput(rotation);
    updateSpot(id, (spot) => {
      const normalized = wrapRotation(sanitized);
      const clampedCenter = clampEntityCenter({ ...spot, rotation: normalized }, value.width, value.height, spot.cx, spot.cy);
      return {
        ...spot,
        rotation: normalized,
        cx: clampedCenter.cx,
        cy: clampedCenter.cy,
      };
    });
  };

  const setGateRotation = (id: string, rotation: number) => {
    const sanitized = sanitizeRotationInput(rotation);
    updateGate(id, (gate) => {
      const normalized = wrapRotation(sanitized);
      const clampedCenter = clampEntityCenter({ ...gate, rotation: normalized }, value.width, value.height, gate.cx, gate.cy);
      return {
        ...gate,
        rotation: normalized,
        cx: clampedCenter.cx,
        cy: clampedCenter.cy,
      };
    });
  };

  const setSpotType = (id: string, type: ParkingSpotType) => {
    updateSpot(id, (spot) => ({
      ...spot,
      type: normalizeSpotType(type),
    }));
  };

  const setGateType = (id: string, type: LayoutBuilderGateType) => {
    updateGate(id, (gate) => ({
      ...gate,
      type: normalizeGateType(type),
    }));
  };

  const renameSpot = (id: string, label: string) => {
    updateSpot(id, (spot) => ({ ...spot, label }));
  };

  const renameGate = (id: string, label: string) => {
    updateGate(id, (gate) => ({ ...gate, label }));
  };

  const dimensionInputClass =
    'w-24 rounded-lg border border-slate-300 px-3 py-2 text-right text-sm font-semibold tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/15 dark:bg-transparent dark:text-slate-100';

  const editorViewportStyle = useMemo(
    () => ({
      width: `${Math.max(value.width * editorScale, 1)}px`,
      height: `${Math.max(value.height * editorScale, 1)}px`,
    }),
    [editorScale, value.height, value.width],
  );

  const editorCanvasStyle = useMemo(
    () => ({
      width: `${value.width}px`,
      height: `${value.height}px`,
      transform: `scale(${editorScale})`,
      transformOrigin: 'top left',
      ...gridBackgroundStyle,
    }),
    [editorScale, value.height, value.width],
  );

  const minimalViewportStyle = useMemo(
    () => ({
      width: `${Math.max(value.width * previewScale, 1)}px`,
      height: `${Math.max(value.height * previewScale, 1)}px`,
    }),
    [previewScale, value.height, value.width],
  );

  const minimalCanvasStyle = useMemo(
    () => ({
      width: `${value.width}px`,
      height: `${value.height}px`,
      transform: `scale(${previewScale})`,
      transformOrigin: 'top left',
      backgroundColor: 'var(--layout-preview-background, #f8fafc)',
    }),
    [previewScale, value.height, value.width],
  );

  const getFullscreenMetrics = useCallback(() => {
    const viewport = fullscreenViewportRef.current;
    if (!viewport) {
      return null;
    }

    const width = viewport.clientWidth;
    const height = viewport.clientHeight;
    const paddedWidth = Math.max(width - 32, 1);
    const paddedHeight = Math.max(height - 32, 1);
    const minScale = Math.max(Math.min(paddedWidth / Math.max(value.width, 1), paddedHeight / Math.max(value.height, 1), 1), 0.1);
    const maxScale = Math.max(minScale * 4, 2);

    return {
      width,
      height,
      minScale,
      maxScale,
    };
  }, [value.height, value.width]);

  const clampFullscreenOffset = useCallback(
    (offset: Point, scale: number): Point => {
      const metrics = getFullscreenMetrics();
      if (!metrics) {
        return offset;
      }

      const scaledWidth = value.width * scale;
      const scaledHeight = value.height * scale;

      return {
        x: scaledWidth <= metrics.width ? (metrics.width - scaledWidth) / 2 : clamp(offset.x, metrics.width - scaledWidth, 0),
        y: scaledHeight <= metrics.height ? (metrics.height - scaledHeight) / 2 : clamp(offset.y, metrics.height - scaledHeight, 0),
      };
    },
    [getFullscreenMetrics, value.height, value.width],
  );

  const applyFullscreenTransform = useCallback(
    (scale: number, offset: Point) => {
      const metrics = getFullscreenMetrics();
      if (!metrics) {
        return;
      }

      const boundedScale = clamp(scale, metrics.minScale, metrics.maxScale);
      const boundedOffset = clampFullscreenOffset(offset, boundedScale);

      setFullscreenTransform({
        scale: boundedScale,
        x: boundedOffset.x,
        y: boundedOffset.y,
      });
    },
    [clampFullscreenOffset, getFullscreenMetrics],
  );

  const resetFullscreenTransform = useCallback(() => {
    const metrics = getFullscreenMetrics();
    if (!metrics) {
      return;
    }

    const scale = metrics.minScale;
    const offset = clampFullscreenOffset(
      {
        x: (metrics.width - value.width * scale) / 2,
        y: (metrics.height - value.height * scale) / 2,
      },
      scale,
    );

    setFullscreenTransform({
      scale,
      x: offset.x,
      y: offset.y,
    });
  }, [clampFullscreenOffset, getFullscreenMetrics, value.height, value.width]);

  const zoomFullscreenToPoint = useCallback(
    (nextScale: number, viewportPoint: Point) => {
      const currentTransform = fullscreenTransformRef.current;
      const safeCurrentScale = Math.max(currentTransform.scale, 0.001);
      const anchor = {
        x: (viewportPoint.x - currentTransform.x) / safeCurrentScale,
        y: (viewportPoint.y - currentTransform.y) / safeCurrentScale,
      };

      applyFullscreenTransform(nextScale, {
        x: viewportPoint.x - anchor.x * nextScale,
        y: viewportPoint.y - anchor.y * nextScale,
      });
    },
    [applyFullscreenTransform],
  );

  const zoomFullscreenByFactor = useCallback(
    (factor: number, anchor?: Point) => {
      const metrics = getFullscreenMetrics();
      if (!metrics) {
        return;
      }

      const viewportPoint = anchor ?? {
        x: metrics.width / 2,
        y: metrics.height / 2,
      };

      zoomFullscreenToPoint(fullscreenTransformRef.current.scale * factor, viewportPoint);
    },
    [getFullscreenMetrics, zoomFullscreenToPoint],
  );

  const beginFullscreenPan = useCallback((pointer: Point) => {
    const currentTransform = fullscreenTransformRef.current;
    fullscreenGestureRef.current = {
      type: 'pan',
      startPointer: pointer,
      startOffset: {
        x: currentTransform.x,
        y: currentTransform.y,
      },
    };
  }, []);

  const beginFullscreenPinch = useCallback((points: Point[]) => {
    if (points.length < 2) {
      return;
    }

    const [firstPoint, secondPoint] = points;
    const currentTransform = fullscreenTransformRef.current;
    const midpoint = midpointBetweenPoints(firstPoint, secondPoint);

    fullscreenGestureRef.current = {
      type: 'pinch',
      startScale: currentTransform.scale,
      startDistance: Math.max(distanceBetweenPoints(firstPoint, secondPoint), 1),
      anchor: {
        x: (midpoint.x - currentTransform.x) / currentTransform.scale,
        y: (midpoint.y - currentTransform.y) / currentTransform.scale,
      },
    };
  }, []);

  const getFullscreenPointerPosition = useCallback((clientX: number, clientY: number): Point | null => {
    const rect = fullscreenViewportRef.current?.getBoundingClientRect();
    if (!rect) {
      return null;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, []);

  const handleFullscreenPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const pointer = getFullscreenPointerPosition(event.clientX, event.clientY);
      if (!pointer) {
        return;
      }

      event.preventDefault();
      fullscreenPointersRef.current.set(event.pointerId, pointer);
      event.currentTarget.setPointerCapture(event.pointerId);

      const activePointers = Array.from(fullscreenPointersRef.current.values());
      if (activePointers.length >= 2) {
        beginFullscreenPinch(activePointers);
      } else {
        beginFullscreenPan(pointer);
      }
    },
    [beginFullscreenPan, beginFullscreenPinch, getFullscreenPointerPosition],
  );

  const handleFullscreenPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (!fullscreenPointersRef.current.has(event.pointerId)) {
        return;
      }

      const pointer = getFullscreenPointerPosition(event.clientX, event.clientY);
      if (!pointer) {
        return;
      }

      event.preventDefault();
      fullscreenPointersRef.current.set(event.pointerId, pointer);

      const activePointers = Array.from(fullscreenPointersRef.current.values());
      if (activePointers.length >= 2) {
        if (fullscreenGestureRef.current?.type !== 'pinch') {
          beginFullscreenPinch(activePointers);
        }

        const gesture = fullscreenGestureRef.current;
        if (!gesture || gesture.type !== 'pinch') {
          return;
        }

        const [firstPoint, secondPoint] = activePointers;
        const scale = gesture.startScale * (distanceBetweenPoints(firstPoint, secondPoint) / gesture.startDistance);
        const midpoint = midpointBetweenPoints(firstPoint, secondPoint);

        applyFullscreenTransform(scale, {
          x: midpoint.x - gesture.anchor.x * scale,
          y: midpoint.y - gesture.anchor.y * scale,
        });
        return;
      }

      if (activePointers.length === 1) {
        if (fullscreenGestureRef.current?.type !== 'pan') {
          beginFullscreenPan(activePointers[0]);
        }

        const gesture = fullscreenGestureRef.current;
        if (!gesture || gesture.type !== 'pan') {
          return;
        }

        applyFullscreenTransform(fullscreenTransformRef.current.scale, {
          x: gesture.startOffset.x + (activePointers[0].x - gesture.startPointer.x),
          y: gesture.startOffset.y + (activePointers[0].y - gesture.startPointer.y),
        });
      }
    },
    [applyFullscreenTransform, beginFullscreenPan, beginFullscreenPinch, getFullscreenPointerPosition],
  );

  const handleFullscreenPointerFinish = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }

      fullscreenPointersRef.current.delete(event.pointerId);
      const activePointers = Array.from(fullscreenPointersRef.current.values());

      if (activePointers.length >= 2) {
        beginFullscreenPinch(activePointers);
        return;
      }

      if (activePointers.length === 1) {
        beginFullscreenPan(activePointers[0]);
        return;
      }

      fullscreenGestureRef.current = null;
    },
    [beginFullscreenPan, beginFullscreenPinch],
  );

  const handleFullscreenWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      event.preventDefault();
      const pointer = getFullscreenPointerPosition(event.clientX, event.clientY);
      if (!pointer) {
        return;
      }

      zoomFullscreenByFactor(event.deltaY < 0 ? 1.15 : 1 / 1.15, pointer);
    },
    [getFullscreenPointerPosition, zoomFullscreenByFactor],
  );

  const renderReadonlySpots = useCallback(
    (spotClassName?: string) =>
      value.spots.map((spot) => {
        const occupancyRaw = spotOccupancy ? spotOccupancy[spot.id] : undefined;
        const occupancyStatus: ReadonlySpotOccupancyStatus =
          typeof occupancyRaw === 'number'
            ? Number.isFinite(occupancyRaw)
              ? occupancyRaw > 0
                ? 1
                : 0
              : null
            : typeof occupancyRaw === 'boolean'
              ? occupancyRaw
                ? 1
                : 0
              : null;
        const spotType = normalizeSpotType(spot.type);
        const appearance = resolveReadonlySpotAppearance(spotType, occupancyStatus);
        const titleLabel = `${spot.label} - ${SPOT_TYPE_LABELS[spotType]} - ${appearance.occupancyLabel}`;
        const rotate = wrapRotation(spot.rotation ?? 0);
        const displayLabel = getSpotDisplayLabel(spot.label);
        const spotIcon = renderReadonlySpotIcon(spotType, `h-5 w-5 ${appearance.iconClassName}`);

        return (
          <div
            key={spot.id}
            className={`absolute ${spotClassName ?? ''}`.trim()}
            style={{
              width: `${spot.width}px`,
              height: `${spot.height}px`,
              left: `${spot.cx}px`,
              top: `${spot.cy}px`,
              transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
              transformOrigin: 'center',
            }}
            title={titleLabel}
          >
            <div
              className={`relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-lg border px-1 py-1 text-center ${appearance.containerClassName}`}
            >
              {spotIcon ? <span className="mb-1 flex h-5 w-5 items-center justify-center">{spotIcon}</span> : null}
              <span
                className={`block w-full max-w-full truncate px-0.5 text-[8px] font-semibold uppercase leading-none tracking-[0.08em] ${appearance.labelClassName}`}
              >
                {displayLabel}
              </span>
            </div>
          </div>
        );
      }),
    [spotOccupancy, value.spots],
  );

  const renderReadonlyGates = useCallback(
    (gateClassName?: string) =>
      gates.map((gate, index) => {
        const rotate = wrapRotation(gate.rotation ?? 0);
        const label = getGateDisplayLabel(gate, index + 1);
        const appearance =
          gate.type === 'entry'
            ? 'border-emerald-500 bg-emerald-100 text-emerald-800 dark:border-emerald-400 dark:bg-emerald-500/15 dark:text-emerald-100'
            : 'border-amber-500 bg-amber-100 text-amber-800 dark:border-amber-400 dark:bg-amber-500/15 dark:text-amber-100';
        return (
          <div
            key={gate.id}
            className={`absolute ${gateClassName ?? ''}`.trim()}
            style={{
              width: `${gate.width}px`,
              height: `${gate.height}px`,
              left: `${gate.cx}px`,
              top: `${gate.cy}px`,
              transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
              transformOrigin: 'center',
            }}
            title={label}
          >
            <div className={`flex h-full w-full items-center justify-center rounded-lg border px-2 text-center ${appearance}`}>
              <span className="block min-w-0 truncate text-[9px] font-semibold uppercase tracking-[0.12em]">{label}</span>
            </div>
          </div>
        );
      }),
    [gates],
  );

  const renderReadonlyLegend = useCallback(
    () => (
      <div className="mt-4 flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-300/80">
        <div className="rounded-2xl border border-slate-200 bg-white/80 px-3 py-3 transition-colors dark:border-white/10 dark:bg-white/5">
          <p className="font-semibold">Status</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-2.5 py-1 dark:border-white/10">
              <span className="h-3 w-3 rounded-sm border border-emerald-500 bg-emerald-500" aria-hidden="true" />
              <span>Wolne</span>
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-2.5 py-1 dark:border-white/10">
              <span className="h-3 w-3 rounded-sm border border-rose-500 bg-rose-500" aria-hidden="true" />
              <span>Zajete</span>
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-2.5 py-1 dark:border-white/10">
              <span className="h-3 w-3 rounded-sm border border-slate-400 bg-slate-500" aria-hidden="true" />
              <span>Brak statusu</span>
            </span>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white/80 px-3 py-3 transition-colors dark:border-white/10 dark:bg-white/5">
          <p className="font-semibold">Typy specjalne</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {READONLY_SPECIAL_TYPES.map((spotType) => {
              const icon = renderReadonlySpotIcon(spotType, 'h-4 w-4 text-slate-700 dark:text-slate-100');
              return (
                <span key={spotType} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-2.5 py-1 dark:border-white/10">
                  {icon ? <span className="flex h-4 w-4 items-center justify-center">{icon}</span> : null}
                  <span>{SPOT_TYPE_LABELS[spotType]}</span>
                </span>
              );
            })}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white/80 px-3 py-3 transition-colors dark:border-white/10 dark:bg-white/5">
          <p className="font-semibold">Ruch</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-2.5 py-1 dark:border-white/10">
              <span className="h-3 w-3 rounded-sm border border-emerald-500 bg-emerald-100 dark:border-emerald-400 dark:bg-emerald-500/20" aria-hidden="true" />
              <span>Wjazd</span>
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-2.5 py-1 dark:border-white/10">
              <span className="h-3 w-3 rounded-sm border border-amber-500 bg-amber-100 dark:border-amber-400 dark:bg-amber-500/20" aria-hidden="true" />
              <span>Wyjazd</span>
            </span>
          </div>
        </div>
      </div>
    ),
    [],
  );

  useEffect(() => {
    if (!isFullscreenOpen) {
      fullscreenPointersRef.current.clear();
      fullscreenGestureRef.current = null;
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const frameId = window.requestAnimationFrame(() => {
      resetFullscreenTransform();
    });
    const handleResize = () => {
      resetFullscreenTransform();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullscreenOpen(false);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(frameId);
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreenOpen, resetFullscreenTransform]);

  const layoutStatus = useMemo(() => {
    const entryCount = gates.filter((gate) => gate.type === 'entry').length;
    const exitCount = gates.filter((gate) => gate.type === 'exit').length;
    const suffixParts = [
      entryCount > 0 ? `${entryCount} ${entryCount === 1 ? 'wjazd' : entryCount < 5 ? 'wjazdy' : 'wjazdow'}` : null,
      exitCount > 0 ? `${exitCount} ${exitCount === 1 ? 'wyjazd' : exitCount < 5 ? 'wyjazdy' : 'wyjazdow'}` : null,
    ].filter((part): part is string => Boolean(part));
    const suffix = suffixParts.length ? ` · ${suffixParts.join(' · ')}` : '';

    if (remainingSpots === null) {
      return `${value.spots.length} miejsc${suffix}`;
    }
    if (remainingSpots === 0) {
      return `Gotowe: ${value.spots.length}/${targetSpots}${suffix}`;
    }
    return `Pozostalo ${remainingSpots} / ${targetSpots}${suffix}`;
  }, [gates, remainingSpots, targetSpots, value.spots.length]);

  if (minimalView) {
    return (
      <>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 transition-colors dark:border-white/10 dark:bg-slate-900/60 max-[425px]:p-3">
          <div className="relative flex w-full items-center justify-center">
            <button
              type="button"
              onClick={() => setIsFullscreenOpen(true)}
              aria-label="Pelny ekran"
              className="absolute right-3 top-3 z-10 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3 py-2 text-slate-600 shadow-sm transition hover:border-emerald-400 hover:text-emerald-600 dark:border-white/10 dark:bg-neutral-900/90 dark:text-slate-100 dark:hover:border-emerald-400"
            >
              <svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                <path d="M2 15.758v-13.758h14.299l5.262 4h-8.769l9.208 9.758-5.701 5.242-9.299-8.749v8.769zm31.752-13.758h14.248v13.809l-4 5.261v-8.768l-10.003 9.208-5.364-5.456 8.626-9.054h-8.769zm14.248 31.752v14.248h-14.299l-5.262-4h8.769l-9.208-10.003 5.701-5.364 9.299 8.626v-8.769zm-31.752 14.248h-14.248v-14.299l4-5.262v8.769l10.003-9.208 5.364 5.701-8.626 9.299h8.769z" />
              </svg>
              <span className="hidden text-[10px] font-semibold uppercase tracking-[0.2em] sm:inline">Pelny ekran</span>
            </button>
            <div ref={previewContainerRef} className="w-full">
              <div
                className="mx-auto overflow-hidden rounded-2xl border border-slate-200/80 shadow-inner transition-colors dark:border-white/10 [--layout-preview-background:#f8fafc] dark:[--layout-preview-background:#020617]"
                style={minimalViewportStyle}
              >
                <div ref={canvasRef} className="relative rounded-2xl" style={minimalCanvasStyle}>
                  {renderReadonlyGates()}
                  {renderReadonlySpots()}
                </div>
              </div>
            </div>
          </div>
          {renderReadonlyLegend()}
        </div>
        {isFullscreenOpen ? (
          <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Schemat parkingu">
            <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => setIsFullscreenOpen(false)} />
            <div className="absolute inset-0 flex flex-col bg-white/95 dark:bg-neutral-950/95" onClick={(event) => event.stopPropagation()}>
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-white/10">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500 dark:text-slate-300/80">Schemat parkingu</p>
                  <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-300/80 md:hidden">Przesuwaj jednym palcem. Powiększaj dwoma palcami.</p>
                  <p className="mt-1 hidden text-[11px] text-slate-500 dark:text-slate-300/80 md:block">
                    Przesuwaj przeciągnięciem. Powiększaj scrollem myszy lub przyciskami.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => zoomFullscreenByFactor(1 / 1.2)}
                    className="rounded-full border border-slate-200 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 transition hover:border-emerald-400 hover:text-emerald-600 dark:border-white/10 dark:text-slate-200 dark:hover:border-emerald-400"
                    aria-label="Pomniejsz"
                  >
                    -
                  </button>
                  <button
                    type="button"
                    onClick={() => zoomFullscreenByFactor(1.2)}
                    className="rounded-full border border-slate-200 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 transition hover:border-emerald-400 hover:text-emerald-600 dark:border-white/10 dark:text-slate-200 dark:hover:border-emerald-400"
                    aria-label="Powieksz"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={resetFullscreenTransform}
                    className="rounded-full border border-slate-200 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 transition hover:border-emerald-400 hover:text-emerald-600 dark:border-white/10 dark:text-slate-200 dark:hover:border-emerald-400"
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsFullscreenOpen(false)}
                    className="rounded-full border border-slate-200 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 transition hover:border-emerald-400 hover:text-emerald-600 dark:border-white/10 dark:text-slate-200 dark:hover:border-emerald-400"
                  >
                    Zamknij
                  </button>
                </div>
              </div>
              <div
                ref={fullscreenViewportRef}
                className="relative flex-1 overflow-hidden bg-slate-100 dark:bg-slate-950"
                style={{ touchAction: 'none' }}
                onPointerDown={handleFullscreenPointerDown}
                onPointerMove={handleFullscreenPointerMove}
                onPointerUp={handleFullscreenPointerFinish}
                onPointerCancel={handleFullscreenPointerFinish}
                onWheel={handleFullscreenWheel}
              >
                <div
                  className="absolute left-0 top-0 rounded-3xl [--layout-preview-background:#f8fafc] dark:[--layout-preview-background:#020617]"
                  style={{
                    width: `${value.width}px`,
                    height: `${value.height}px`,
                    transform: `translate(${fullscreenTransform.x}px, ${fullscreenTransform.y}px) scale(${fullscreenTransform.scale})`,
                    transformOrigin: 'top left',
                    backgroundColor: 'var(--layout-preview-background, #f8fafc)',
                  }}
                >
                  {renderReadonlyGates('pointer-events-none')}
                  {renderReadonlySpots('pointer-events-none')}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,0.85fr)]">
      <div className="min-w-0 space-y-4">
        <div className="rounded-2xl border border-slate-200 p-4 transition-colors dark:border-white/10 dark:bg-white/5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-500 dark:text-slate-300/80">Obszar roboczy</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 dark:text-slate-300/80">
                Wymiary: {value.width}px × {value.height}px · {layoutStatus}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2"></div>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
              <span>Szerokość (px)</span>
              <input
                type="number"
                min={120}
                step={10}
                value={value.width}
                onChange={(event) =>
                  emitChange({
                    ...value,
                    width: Number(event.target.value) || DEFAULT_CANVAS.width,
                  })
                }
                className={dimensionInputClass}
              />
            </label>
            <label className="flex flex-col gap-2 text-xs uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
              <span>Wysokość (px)</span>
              <input
                type="number"
                min={120}
                step={10}
                value={value.height}
                onChange={(event) =>
                  emitChange({
                    ...value,
                    height: Number(event.target.value) || DEFAULT_CANVAS.height,
                  })
                }
                className={dimensionInputClass}
              />
            </label>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-inner transition-colors dark:border-white/10 dark:bg-white/10">
          <div className="mb-3 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-500 dark:text-slate-300/80">
            <span>{layoutStatus}</span>
          </div>
          <div
            ref={editorContainerRef}
            className="min-w-0 overflow-x-auto overflow-y-visible rounded-2xl border border-slate-200 bg-slate-50 transition-colors dark:border-white/10 dark:bg-slate-900/60"
          >
            <div className="relative mx-auto rounded-2xl" style={editorViewportStyle}>
              <div
                ref={canvasRef}
                className="absolute left-0 top-0 rounded-2xl [--grid-background:#0f172a] [--grid-line-soft:rgba(148,163,184,0.08)] [--grid-line-strong:rgba(248,250,252,0.22)] dark:[--grid-background:#020617] dark:[--grid-line-soft:rgba(94,234,212,0.08)] dark:[--grid-line-strong:rgba(94,234,212,0.2)]"
                style={editorCanvasStyle}
              >
                {allEntities.map((entity, index) => {
                  const isSelected = selectedId === entity.id;
                  const rotate = wrapRotation(entity.rotation ?? 0);
                  const isGate = isLayoutGate(entity);
                  const gateLabel = isGate ? getGateDisplayLabel(entity, index + 1) : null;
                  const buttonClasses = isGate
                    ? isSelected
                      ? entity.type === 'entry'
                        ? 'border-emerald-400 bg-emerald-500 text-white'
                        : 'border-amber-400 bg-amber-500 text-white'
                      : entity.type === 'entry'
                        ? 'border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-400/70 dark:bg-emerald-500/20 dark:text-emerald-100'
                        : 'border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-400/70 dark:bg-amber-500/20 dark:text-amber-100'
                    : isSelected
                      ? 'border-emerald-400 bg-emerald-500 text-white'
                      : 'border-slate-400/70 bg-slate-500/90 text-white';
                  return (
                    <div
                      key={entity.id}
                      className="absolute"
                      style={{
                        width: `${entity.width}px`,
                        height: `${entity.height}px`,
                        left: `${entity.cx}px`,
                        top: `${entity.cy}px`,
                        transform: `translate(-50%, -50%) rotate(${rotate}deg)`,
                        transformOrigin: 'center',
                      }}
                    >
                      <button
                        type="button"
                        disabled={disabled}
                        onPointerDown={(event) => handleDragPointerDown(event, entity)}
                        onPointerMove={handlePointerMove}
                        onPointerUp={finishDragging}
                        onPointerCancel={finishDragging}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedId(entity.id);
                        }}
                        className={`flex h-full w-full select-none items-center justify-center rounded-lg border px-2 text-center text-[9px] font-semibold uppercase tracking-[0.15em] transition ${buttonClasses}`}
                        style={{ touchAction: 'none' }}
                      >
                        {isGate ? (
                          <span className="max-w-full truncate leading-none">{gateLabel}</span>
                        ) : (
                          entity.label
                        )}
                      </button>
                      {isSelected && (
                        <div className="pointer-events-none absolute inset-0 overflow-visible">
                          <div className="pointer-events-none absolute -top-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1">
                            <span className="block h-5 w-[2px] bg-emerald-400" />
                            <button
                              type="button"
                              onPointerDown={(event) => handleRotationPointerDown(event, entity)}
                              onPointerMove={handlePointerMove}
                              onPointerUp={finishDragging}
                              onPointerCancel={finishDragging}
                              className="pointer-events-auto h-4 w-4 cursor-grab rounded-full border-2 border-white bg-emerald-500 shadow-lg"
                              style={{ touchAction: 'none' }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="min-w-0 flex flex-col gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white/80 p-3 transition-colors dark:border-white/10 dark:bg-white/5 sm:p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-500 dark:text-slate-300/80">Lista miejsc</p>
            <button
              type="button"
              onClick={addSpot}
              disabled={!canAddSpot}
              className="rounded-full border border-emerald-500 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Dodaj miejsce
            </button>
          </div>
          <div className="mt-3 space-y-3 sm:mt-4">
            {value.spots.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-[11px] uppercase tracking-[0.3em] text-slate-400 dark:border-white/20 dark:text-slate-300/80">
                Brak miejsc w schemacie
              </p>
            ) : (
              value.spots.map((spot) => {
                const isSelected = selectedId === spot.id;
                const { width, height } = rotatedDimensions(spot);
                const spotType = normalizeSpotType(spot.type);
                return (
                  <div
                    key={spot.id}
                    className={`rounded-2xl border px-3 py-2.5 text-[11px] uppercase tracking-[0.2em] transition sm:px-4 sm:py-3 sm:text-xs sm:tracking-[0.25em] ${
                      isSelected
                        ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-400/60 dark:bg-emerald-500/10'
                        : 'border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/5'
                    }`}
                  >
                    <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
                      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                        <input
                          type="text"
                          value={spot.label}
                          maxLength={8}
                          onFocus={() => setSelectedId(spot.id)}
                          onChange={(event) => renameSpot(spot.id, event.target.value)}
                          className="w-full min-w-0 rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100 sm:w-24 sm:text-[11px] sm:tracking-[0.3em]"
                        />
                        <label className="flex items-center gap-1 text-[9px] font-semibold tracking-[0.16em] text-slate-500 dark:text-slate-300/80 sm:text-[10px] sm:tracking-[0.2em]">
                          <span>Kat</span>
                          <input
                            type="number"
                            step={1}
                            min={0}
                            max={360}
                            value={Math.round(wrapRotation(spot.rotation ?? 0))}
                            onFocus={() => setSelectedId(spot.id)}
                            onChange={(event) => setSpotRotation(spot.id, Number(event.target.value))}
                            className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-right text-[10px] font-semibold tracking-[0.16em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100 sm:w-20 sm:text-[11px] sm:tracking-[0.2em]"
                          />
                        </label>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-[9px] text-slate-500 dark:text-slate-300/80 sm:text-[10px]">
                        <span>
                          X: {Math.round(spot.cx)} · Y: {Math.round(spot.cy)}
                        </span>
                        <span>
                          {Math.round(width)}×{Math.round(height)} px
                        </span>
                      </div>
                    </div>
                    <div className="mt-2.5 flex flex-col gap-2 sm:mt-3 sm:flex-row sm:flex-wrap sm:items-center">
                      <label className="flex min-w-0 flex-col gap-1 text-[9px] font-semibold tracking-[0.16em] text-slate-500 dark:text-slate-300/80 sm:flex-row sm:items-center sm:gap-2 sm:text-[10px] sm:tracking-[0.2em]">
                        <span>Typ</span>
                        <select
                          value={spotType}
                          onChange={(event) => setSpotType(spot.id, event.target.value as ParkingSpotType)}
                          className="min-w-0 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/15 dark:bg-slate-900/80 dark:text-slate-100 dark:focus:border-emerald-300 sm:px-3 sm:text-[11px] sm:tracking-[0.2em]"
                        >
                          {SPOT_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        onClick={() => removeSpot(spot.id)}
                        className="rounded-full border border-rose-300 px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.2em] text-rose-500 transition hover:bg-rose-500 hover:text-white sm:ml-auto sm:text-[10px] sm:tracking-[0.3em]"
                      >
                        Usuń
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white/80 p-3 transition-colors dark:border-white/10 dark:bg-white/5 sm:p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-500 dark:text-slate-300/80">Wjazdy i wyjazdy</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => addGate('entry')}
                disabled={disabled}
                className="rounded-full border border-emerald-500 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-600 transition hover:bg-emerald-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Dodaj wjazd
              </button>
              <button
                type="button"
                onClick={() => addGate('exit')}
                disabled={disabled}
                className="rounded-full border border-amber-500 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-600 transition hover:bg-amber-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Dodaj wyjazd
              </button>
            </div>
          </div>
          <div className="mt-3 space-y-3 sm:mt-4">
            {gates.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-center text-[11px] uppercase tracking-[0.3em] text-slate-400 dark:border-white/20 dark:text-slate-300/80">
                Brak wjazdow i wyjazdow w schemacie
              </p>
            ) : (
              gates.map((gate, index) => {
                const isSelected = selectedId === gate.id;
                const { width, height } = rotatedDimensions(gate);
                const displayLabel = getGateDisplayLabel(gate, index + 1);
                const accentClasses =
                  gate.type === 'entry'
                    ? isSelected
                      ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-400/60 dark:bg-emerald-500/10'
                      : 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-400/20 dark:bg-emerald-500/5'
                    : isSelected
                      ? 'border-amber-400 bg-amber-50 dark:border-amber-400/60 dark:bg-amber-500/10'
                      : 'border-amber-200 bg-amber-50/60 dark:border-amber-400/20 dark:bg-amber-500/5';

                return (
                  <div
                    key={gate.id}
                    className={`rounded-2xl border px-3 py-2.5 text-[11px] uppercase tracking-[0.2em] transition sm:px-4 sm:py-3 sm:text-xs sm:tracking-[0.25em] ${accentClasses}`}
                  >
                    <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3">
                      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                        <input
                          type="text"
                          value={gate.label}
                          maxLength={20}
                          placeholder={displayLabel}
                          onFocus={() => setSelectedId(gate.id)}
                          onChange={(event) => renameGate(gate.id, event.target.value)}
                          className="w-full min-w-0 rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100 sm:w-32 sm:text-[11px] sm:tracking-[0.3em]"
                        />
                        <label className="flex items-center gap-1 text-[9px] font-semibold tracking-[0.16em] text-slate-500 dark:text-slate-300/80 sm:text-[10px] sm:tracking-[0.2em]">
                          <span>Kat</span>
                          <input
                            type="number"
                            step={1}
                            min={0}
                            max={360}
                            value={Math.round(wrapRotation(gate.rotation ?? 0))}
                            onFocus={() => setSelectedId(gate.id)}
                            onChange={(event) => setGateRotation(gate.id, Number(event.target.value))}
                            className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-right text-[10px] font-semibold tracking-[0.16em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/10 dark:bg-white/10 dark:text-slate-100 sm:w-20 sm:text-[11px] sm:tracking-[0.2em]"
                          />
                        </label>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-[9px] text-slate-500 dark:text-slate-300/80 sm:text-[10px]">
                        <span>
                          X: {Math.round(gate.cx)} · Y: {Math.round(gate.cy)}
                        </span>
                        <span>
                          {Math.round(width)}×{Math.round(height)} px
                        </span>
                      </div>
                    </div>
                    <div className="mt-2.5 flex flex-col gap-2 sm:mt-3 sm:flex-row sm:flex-wrap sm:items-center">
                      <label className="flex min-w-0 flex-col gap-1 text-[9px] font-semibold tracking-[0.16em] text-slate-500 dark:text-slate-300/80 sm:flex-row sm:items-center sm:gap-2 sm:text-[10px] sm:tracking-[0.2em]">
                        <span>Typ</span>
                        <select
                          value={gate.type}
                          onFocus={() => setSelectedId(gate.id)}
                          onChange={(event) => setGateType(gate.id, event.target.value as LayoutBuilderGateType)}
                          className="min-w-0 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-700 transition focus:border-emerald-400 focus:outline-none dark:border-white/15 dark:bg-slate-900/80 dark:text-slate-100 dark:focus:border-emerald-300 sm:px-3 sm:text-[11px] sm:tracking-[0.2em]"
                        >
                          {GATE_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        onClick={() => removeGate(gate.id)}
                        className="rounded-full border border-rose-300 px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.2em] text-rose-500 transition hover:bg-rose-500 hover:text-white sm:ml-auto sm:text-[10px] sm:tracking-[0.3em]"
                      >
                        Usuń
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        {targetSpots && targetSpots > 0 ? (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 text-[11px] uppercase tracking-[0.3em] text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-500/10 dark:text-emerald-200">
            Dodaj miejsca: {value.spots.length}/{targetSpots}. {remainingSpots === 0 ? 'Limit spełniony.' : `Pozostalo ${remainingSpots}.`}
          </div>
        ) : null}
      </div>
    </div>
  );
}
